import React, { useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ShoppingBagIcon, XIcon } from 'lucide-react';
import { useNavDrawer } from '../../contexts/NavDrawerContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useCart } from '../../contexts/CartContext';
import { drawerNav } from '../../data/navigation';
import { Wordmark } from '../brand/Wordmark';
import { ShopNowButton } from '../shop/ShopNowButton';
const EASE = [0.23, 1, 0.32, 1] as const;
export function NavDrawer() {
  const {
    isOpen,
    close,
    triggerRef
  } = useNavDrawer();
  const {
    t,
    lang
  } = useLanguage();
  const {
    totals
  } = useCart();
  const {
    pathname
  } = useLocation();
  const reduce = useReducedMotion();
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  // Close when the route changes.
  useEffect(() => {
    close();
  }, [pathname, close]);

  // Focus management, Escape, focus trap, scroll lock.
  useEffect(() => {
    if (!isOpen) return;
    const previous = document.activeElement as HTMLElement | null;
    const raf = requestAnimationFrame(() => closeRef.current?.focus());
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const focusables = Array.from(panelRef.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled])'));
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      // Restore focus to the trigger only if focus hasn't intentionally moved elsewhere
      // (e.g. SHOP NOW moved it to the shop section).
      const active = document.activeElement;
      const focusLeftDrawer = active && active !== document.body && !panelRef.current?.contains(active);
      if (!focusLeftDrawer) {
        const target = triggerRef.current ?? previous;
        if (target && document.contains(target)) target.focus({
          preventScroll: true
        });
      }
    };
  }, [isOpen, close, triggerRef]);
  return <AnimatePresence data-id="element-473" data-mp="3up2tba3siyiv">
      {isOpen && <div className="fixed inset-0 z-[60]" data-id="element-474" data-mp="3uo16679i2lks">
          <motion.div className="absolute inset-0 bg-[#030A14]/70 backdrop-blur-sm" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={close} aria-hidden data-id="element-475" data-mp="3jknpisah0ud" />
          <motion.div ref={panelRef} id="site-nav-drawer" role="dialog" aria-modal="true" aria-label={lang === 'es' ? 'Navegación del sitio' : 'Site navigation'} className="absolute inset-y-0 right-0 flex w-full max-w-[440px] flex-col border-l border-marine/25 bg-navy shadow-2xl" initial={reduce ? {
        opacity: 0
      } : {
        x: '100%'
      }} animate={reduce ? {
        opacity: 1
      } : {
        x: 0
      }} exit={reduce ? {
        opacity: 0
      } : {
        x: '100%'
      }} transition={{
        duration: 0.28,
        ease: EASE
      }} data-id="element-476" data-mp="50ggnla4dacm">
            <div className="flex h-[72px] items-center justify-between border-b mf-hairline px-6" data-id="element-477" data-mp="2ppnvqroybzsn">
              <Wordmark data-id="element-478" data-mp="2kqb6k0alygmw" />
              <button ref={closeRef} type="button" onClick={close} aria-label={lang === 'es' ? 'Cerrar menú' : 'Close menu'} className="flex h-11 w-11 items-center justify-center rounded-md text-titanium transition-colors duration-150 hover:bg-white/10 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-ice" data-id="element-479" data-mp="2kqt9tgvotv4b">
                <XIcon className="h-6 w-6" data-id="element-480" data-mp="3h4kt7fd8im58" />
              </button>
            </div>

            <nav aria-label={lang === 'es' ? 'Principal' : 'Main'} className="flex-1 overflow-y-auto px-6 py-6" data-id="element-481" data-mp="3h6x9zp79aqgp">
              <ul data-id="element-482" data-mp="3r2qubur7vo91">
                {drawerNav.map((item, i) => <motion.li key={item.to} initial={reduce ? false : {
              opacity: 0,
              x: 16
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.22,
              delay: reduce ? 0 : 0.06 + i * 0.035,
              ease: EASE
            }} data-id="element-483" data-mp="3r3ikgq21otrx" data-mpi={i}>
                    <NavLink to={item.to} end={item.to === '/'} onClick={close} className={({
                isActive
              }) => `group flex items-center gap-4 border-b mf-hairline py-4 font-display text-[30px] font-semibold uppercase leading-none tracking-[0.05em] transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ice ${isActive ? 'text-marine' : 'text-white hover:text-ice'}`} data-id="element-484" data-mp="21pww8oze7064">
                      {({
                  isActive
                }) => <>
                          <span aria-hidden className={`h-6 w-[3px] rounded-full ${isActive ? 'bg-marine' : 'bg-transparent'}`} data-id="element-485" data-mp="sp16nhpr2tbv" />
                          <span aria-current={isActive ? 'page' : undefined} data-id="element-486" data-mp="3deqhljnc1uza">{t(item.key)}</span>
                        </>}
                    </NavLink>
                  </motion.li>)}
              </ul>
            </nav>

            <div className="space-y-5 border-t mf-hairline px-6 py-6" data-id="element-487" data-mp="3ewywvx6av6aj">
              <ShopNowButton source="drawer" showPrice onActivate={close} className="w-full" data-id="element-488" data-mp="30z0l8zjd5nyq" />
              <Link to="/order-tracking" onClick={close} className="block text-sm text-titanium hover:text-white" data-id="element-489" data-mp="35x2egdai73nx">
                {t('nav.track')}
              </Link>
              <div className="flex items-center justify-end" data-id="element-490" data-mp="2r6ut9pt436jj">
                <Link to="/cart" onClick={close} className="inline-flex items-center gap-2 text-sm text-titanium hover:text-white" data-id="element-491" data-mp="2w061nvdknvdk">
                  <ShoppingBagIcon className="h-4 w-4" data-id="element-492" data-mp="2mae36q2cqi3t" />
                  {lang === 'es' ? 'Carrito' : 'Cart'} ({totals.lights})
                </Link>
              </div>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}